/**
 * CREATIVE DETONATOR - Shared DOM Utilities
 * Helper functions for modal injection and DOM operations
 *
 * @version 1.1.0 - Now uses centralized sanitize.js for XSS prevention
 */

import { escapeHtml } from '../core/sanitize.js';

// v1.1 - Modal injection helper for self-rendering modules

/**
 * Inject modal HTML into the DOM if it doesn't already exist
 * @param {string} modalId - The ID of the modal element (without #)
 * @param {string} html - The HTML string to inject
 * @param {string} [container] - Optional container selector (defaults to body)
 * @returns {HTMLElement|null} The modal element
 */
export function injectModalHtml(modalId, html, container = 'body') {
  // Don't inject if already exists
  if (document.getElementById(modalId)) {
    return document.getElementById(modalId);
  }

  const target = document.querySelector(container);
  if (!target) {
    console.warn(`[DOMUtils] Container "${container}" not found for modal ${modalId}`);
    return null;
  }

  target.insertAdjacentHTML('beforeend', html);
  console.log(`[DOMUtils] Injected modal: ${modalId}`);
  return document.getElementById(modalId);
}

/**
 * Remove a modal from the DOM
 * @param {string} modalId - The ID of the modal element (without #)
 */
export function removeModal(modalId) {
  const modal = document.getElementById(modalId);
  if (modal) {
    modal.remove();
    console.log(`[DOMUtils] Removed modal: ${modalId}`);
  }
}

/**
 * Generate Mac-style window titlebar HTML
 * Follows the classic Mac OS window pattern with dithered background and pill title
 *
 * @param {Object} options - Titlebar configuration
 * @param {string} options.title - Title text (without diamond prefix)
 * @param {string} [options.closeId] - ID for close button (for event binding)
 * @param {boolean} [options.showClose=true] - Whether to show close button
 * @param {string} [options.titlePrefix='◆ '] - Prefix for title (diamond by default)
 * @param {string} [options.className=''] - Additional class for titlebar
 * @returns {string} - HTML string for the titlebar
 */
export function createWindowTitlebar({
  title,
  closeId = null,
  showClose = true,
  titlePrefix = '◆ ',
  className = ''
}) {
  const closeHtml = showClose
    ? `<div class="window-close modal-close-btn"${closeId ? ` id="${closeId}"` : ''} aria-label="Close"></div>`
    : '';

  const classAttr = className ? `window-titlebar ${className}` : 'window-titlebar';

  return `
    <div class="${classAttr}">
      ${closeHtml}
      <div class="window-title">${escapeHtml(titlePrefix)}${escapeHtml(title)}</div>
    </div>
  `;
}

// escapeHtml is now imported from ../core/sanitize.js
// Removed local duplicate implementation for FRONTEND-21 (XSS Prevention)

// Global export for non-module usage
window.DOMUtils = {
  injectModalHtml,
  removeModal,
  createWindowTitlebar
};
